import { useMemo, ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Box, styled } from '@mui/material'
import { CopiableComponent, EllipsisWithTooltip, IconButton, Link, Typography } from '@carto/meridian-ds/components'
import { cleanUrl, downloadUrlAsFile, getCommonUrlPart } from 'features/common/utils/urlUtils'
import DownloadIcon from 'assets/icons/icon-file-download.svg?react'

const CELL_HEIGHT = '40px'
const CELL_WIDTH = '48px'
const TABLE_MAX_HEIGHT = `${40 * 4 + 10}px`

const Container = styled(Box)(({ theme }) => ({
  '&:not(:first-of-type)': {
    paddingTop: theme.spacing(1.5)
  }
}))

const TableHeader = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  height: CELL_WIDTH,
  padding: theme.spacing(0, 2),
  border: `1px solid ${theme.palette.divider}`
}))

const TableWrapper = styled(Box)(() => ({
  borderRight: 'none'
}))

const TableBox = styled(Box)(() => ({
  maxHeight: TABLE_MAX_HEIGHT,
  overflow: 'auto'
}))

const TableRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: CELL_HEIGHT,
  border: `1px solid ${theme.palette.divider}`,
  borderBottom: 'none',
  '&:first-of-type': {
    borderTop: 'none'
  },
  '&:last-of-type': {
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.divider}`
  }
}))

const TableCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 'none',
  width: CELL_WIDTH,
  textAlign: 'center',
  borderRight: `1px solid ${theme.palette.divider}`,
  '&:nth-of-type(2)': {
    flex: '1 1 auto',
    minWidth: '1px'
  },
  '&:last-of-type': {
    borderRight: 'none'
  }
}))

const DownloadLink = styled(Link)(({ theme }) => ({
  minWidth: '1px',
  padding: theme.spacing(0, 1.5)
}))

interface DownloadsPartProps {
  title?: ReactNode
  downloadUrls: string[]
  availableUntil?: Date
  isLoading?: boolean
}

function DownloadsPart({ title, downloadUrls, availableUntil, isLoading }: DownloadsPartProps) {
  const intl = useIntl()

  const { commonUrlPart, downloads } = useMemo(() => {
    const cleanUrls = downloadUrls.map(cleanUrl)
    const commonUrlPart = getCommonUrlPart(cleanUrls)
    return {
      commonUrlPart,
      downloads: downloadUrls.map((url, i) => ({
        url,
        filename: decodeURIComponent(cleanUrls[i].substring(commonUrlPart.length))
      }))
    }
  }, [downloadUrls])

  return (
    <Container>
      <TableHeader>
        <Typography variant="caption" weight="medium">
          {title ? title : <FormattedMessage id="jobs.download.parts" values={{ count: downloads.length }} />}
        </Typography>
        {availableUntil && (
          <Typography variant="caption" color="textSecondary" sx={{ marginLeft: 'auto' }}>
            <FormattedMessage
              id="jobs.download.availableUntil"
              values={{
                date: intl.formatDate(availableUntil, {
                  day: 'numeric',
                  month: 'numeric',
                  year: 'numeric'
                }),
                hour: intl.formatTime(availableUntil, {
                  hour: 'numeric',
                  minute: 'numeric'
                })
              }}
            />
          </Typography>
        )}
      </TableHeader>
      <TableWrapper>
        <TableBox mr={downloads.length > 4 ? '-5px' : 0}>
          {downloads.map(({ url, filename }, i) => (
            <TableRow key={i}>
              <TableCell>
                <Typography variant="caption" color="textSecondary">
                  {i + 1}
                </Typography>
              </TableCell>
              <TableCell>
                <DownloadLink
                  href={isLoading ? '' : url}
                  download={filename}
                  variant="caption"
                  color={isLoading ? 'text.disabled' : undefined}
                >
                  <EllipsisWithTooltip ellipsisTarget="first-child">
                    <span>{commonUrlPart}</span>
                    <span>{filename}</span>
                  </EllipsisWithTooltip>
                </DownloadLink>
              </TableCell>
              <TableCell>
                <CopiableComponent button value={url} disabled={isLoading} />
              </TableCell>
              <TableCell>
                <IconButton
                  tooltip={intl.formatMessage({ id: 'common.button.download' })}
                  tooltipPlacement="top"
                  disabled={isLoading}
                  icon={<DownloadIcon />}
                  onClick={() => downloadUrlAsFile(url, filename)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBox>
      </TableWrapper>
    </Container>
  )
}

export default DownloadsPart
